
/*  Floating point numbers cannot represent all decimals precisely in binary.
 *  This can lead to unexpected results, such as 0.1 + 0.2 === 0.3 returning false.
 *  https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toFixed
 *  2.55.toFixed(1) returns '2.5'. Note that it rounds down.
 *  
 *  It rounds the number value parameter to maximum decimals
 */
export const toFixed = (val: number, decimals: number = 0): number => {
  if (!isFloat(val)) {
    return val;
  }
  const multiplier = Math.pow(10, decimals);
  return Math.round(Number((val * multiplier).toFixed(decimals))) / multiplier;
};

export const getNumberOfDecimals = (val: string, separator: string = '.'): number => {
  const decimalInput = val?.split(separator)[1];
  return Number(decimalInput?.length) || 0;
};

export const getNumberOfIntegerDigits = (val: string, separator: string = '.'): number => {
  const decimalInput = val?.split('-').slice(-1)?.[0].split(separator)?.[0];
  return Number(decimalInput?.length) || 0;
};

export const isFloat = (val: number | string, separator: string = '.'): boolean => {
  const numericValue = Number(val);
  return typeof val === 'number' && !Number.isInteger(val) ||
    typeof val === 'string' && val.includes(separator) && !Number.isNaN(numericValue) && numericValue !== 0;
};

export const isNumberExponential = (val: string): boolean => val.split(/[eE]/).length > 1;

export const roundToMaxDecimals = (
  val: number | '',
  maxDecimals: number = 0
): string => {
  const numericValue: number = Number(val);
  const stringValue: string = String(numericValue);
  
  let actualDecimals: number;
  if (isNumberExponential(stringValue)) {
    // calculate decimals of i.e. 3.43434324e-13 = 13 + '43434324'.length = 21
    const splitExponentialValue = stringValue.split(/[eE]-/);
    const exponent = parseInt(splitExponentialValue?.[1], 10);
    const numberOfDecimals = exponent + (splitExponentialValue[0].split('.')?.[1]?.length || 0);
    actualDecimals = Math.min(numberOfDecimals, maxDecimals);
  } else {
    actualDecimals = Math.min(getNumberOfDecimals(stringValue), maxDecimals);
  }
  return toFixed(numericValue, actualDecimals).toFixed(actualDecimals);
};

// The pattern matches one or more zeros after the dot ".0" or one or more zeros after a number at the end of the string
// removes always the last zero digit characters not followed by a number. "123.000" => 123 and "123.003000" => 123.003
const trailingZerosPattern = /(\.0+|0+)$/;

export const floatToString = (
  val: number | '',
  separator: string = '.',
  maxDecimals: number = 0
): string => {
  return roundToMaxDecimals(val, maxDecimals).replace(trailingZerosPattern, '').replace('.', separator);
};

export const replaceSeparatorWithDot = (val: string, separator: string ): string => {
  return val?.replace(separator, '.');
};
